import { PaymentProfileTypes } from 'APIs/constants';
import { activatePaymentProfile, addNewPaymentProfile, getMerchantPaymentProfiles } from 'APIs/merchants/merchants';
import { PaymentProfile, PaymentProfileType } from 'APIs/merchants/merchants.paymentprofiles.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import Spinner from 'Components/Spinner/Spinner';
import { H2, Label, LpBox, LpDetails, Row } from 'Constants/styles';
import { FormEvent, MouseEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import getErrorMessage from 'utils/getErrorMessage';

import { ActivateBtn, EditBtn, EditFormRow, FormControl, PPRow, SpinnerWrapper } from './styles';

type Props = {
  merchantId: string;
};

const PaymentProfiles = ({ merchantId }: Props): ReactElement => {
  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [paymentProfiles, setPaymentProfiles] = useState<Array<PaymentProfile>>([]);
  const [availableProfileIds, setAvailableProfileIds] = useState<Array<string>>([]);
  const [newProfileType, setNewProfileType] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [activateIsLoading, setActivateIsLoading] = useState<boolean>(false);
  const [activatingProfileId, setActivatingProfileId] = useState<string>('');
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const fetchPaymentProfiles = useCallback(async () => {
    if (!apiBaseUri || !merchantId) {
      return;
    }

    try {
      setIsLoading(true);
      const resp = await getMerchantPaymentProfiles(apiBaseUri, merchantId);
      setPaymentProfiles(resp);
    } catch (e) {
      setModalErrorMsg(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUri, merchantId]);

  const handleAddProfile = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      try {
        if (newProfileType !== '') {
          setModalIsLoading(true);
          await addNewPaymentProfile(apiBaseUri, merchantId, newProfileType as PaymentProfileType);
          setNewProfileType('');
          fetchPaymentProfiles();
          setShowModal(false);
        }
      } catch (e) {
        setModalErrorMsg(getErrorMessage(e));
      } finally {
        setModalIsLoading(false);
      }
    },
    [apiBaseUri, fetchPaymentProfiles, merchantId, newProfileType],
  );

  const handleProfileActivation = useCallback(
    async (event: MouseEvent<HTMLButtonElement>, profileId: string) => {
      event.preventDefault();

      try {
        setActivatingProfileId(profileId);
        setActivateIsLoading(true);
        await activatePaymentProfile(apiBaseUri, merchantId, profileId);
        fetchPaymentProfiles();
      } catch (e) {
        setModalErrorMsg(getErrorMessage(e));
      } finally {
        setActivateIsLoading(false);
      }
    },
    [apiBaseUri, fetchPaymentProfiles, merchantId],
  );

  const toggleModal = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(true);
    setModalIsLoading(false);
    setModalErrorMsg('');
  };

  const handleCloseModalMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setModalErrorMsg('');
  };

  const handleCancel = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(false);
  };

  useEffect(() => {
    fetchPaymentProfiles();
  }, [fetchPaymentProfiles]);

  useEffect(() => {
    setAvailableProfileIds(
      Object.keys(PaymentProfileTypes).filter((key) => !paymentProfiles.find((profile) => profile.profileType === key)),
    );
  }, [paymentProfiles]);

  useEffect(() => {
    if (newProfileType === '' && availableProfileIds.length > 0) {
      setNewProfileType(availableProfileIds[0]);
    }
  }, [availableProfileIds, newProfileType]);

  return (
    <LpBox>
      <Row>
        <div className="col-sm-7">
          <H2 className="mb-0">Payment profiles</H2>
        </div>
        <div className="col-sm-5 text-right">
          <EditBtn onClick={toggleModal} data-testid="add-new-profile-btn">
            Add new profile
          </EditBtn>
        </div>
      </Row>
      {isLoading && (
        <SpinnerWrapper>
          <Spinner color="#0016D1" />
        </SpinnerWrapper>
      )}
      {!isLoading && paymentProfiles.length > 0 && (
        <LpDetails data-testid="payment-profiles-list">
          <Row className="pt-4">
            <div className="col-sm-3">
              <Label>Profile ID</Label>
            </div>
            <div className="col-sm-3">
              <Label>Type</Label>
            </div>
            <div className="col-sm-3">
              <Label>Created At</Label>
            </div>
            <div className="col-sm-3">
              <Label>Status</Label>
            </div>
          </Row>
          {paymentProfiles.map((profile) =>
            profile.status === 'active' ? (
              <PPRow className="pt-4" key={profile._id}>
                <div className="col-sm-3 ml-0">{profile._id}</div>
                <div className="col-sm-3">{PaymentProfileTypes[profile.profileType]?.label ?? profile.profileType}</div>
                <div className="col-sm-3">{profile.createdAt}</div>
                <div className="col-sm-3">Active</div>
              </PPRow>
            ) : null,
          )}
          {paymentProfiles.map((profile) =>
            profile.status !== 'active' ? (
              <PPRow className="pt-4" key={profile._id}>
                <div className="col-sm-3">{profile._id}</div>
                <div className="col-sm-3">{PaymentProfileTypes[profile.profileType]?.label ?? profile.profileType}</div>
                <div className="col-sm-3">{profile.createdAt}</div>
                <div className="col-sm-3">
                  {profile.status === 'inactive' && (
                    <ActivateBtn
                      onClick={(e) => handleProfileActivation(e, profile._id)}
                      disabled={activateIsLoading}
                      data-testid="profile-activation-btn"
                    >
                      {activateIsLoading && profile._id === activatingProfileId ? 'Activating...' : 'Activate'}
                    </ActivateBtn>
                  )}
                </div>
              </PPRow>
            ) : null,
          )}
        </LpDetails>
      )}
      {showModal && (
        <Modal
          title="Add payment profile"
          cancelBtnText="Cancel"
          confirmBtnText="Add"
          handleCancel={handleCancel}
          handleSubmit={handleAddProfile}
          disableConfirmBtn={modalIsLoading}
          isLoading={modalIsLoading}
          large={true}
          testId={'payment-profile-modal'}
        >
          {modalErrorMsg.length > 0 && (
            <Message
              className="mt-2 mb-0"
              success={false}
              description={modalErrorMsg}
              handleClose={handleCloseModalMsg}
            />
          )}
          <LpDetails className="pt-2 pb-0">
            <EditFormRow className="mt-2">
              <FormControl
                value={newProfileType}
                onChange={(e) => setNewProfileType(e.target.value)}
                data-testid="modal-payment-profile-select"
              >
                {availableProfileIds.map((key) => (
                  <option key={key} value={key}>
                    {PaymentProfileTypes[key as keyof typeof PaymentProfileTypes]?.label ?? key}
                  </option>
                ))}
              </FormControl>
            </EditFormRow>
          </LpDetails>
        </Modal>
      )}
    </LpBox>
  );
};

export default PaymentProfiles;
